import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { apiRequest } from "@/lib/queryClient";
import { FONT } from "@shared/brand";

const schema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  // D-170.6: converted from a raw apiRequest. Class A — each fire INSERTs a fresh reset token
  // and sends an email (auth-routes.ts:1257-1300). Old tokens stay valid, so a double-submit
  // does not break the first link; it does send two emails. The route is rate-limited to
  // 1/hour per address, so the second fire of a double-click can also burn that allowance.
  const resetFlight = useSingleFlight();
  const resetMutation = useMutation({
    mutationFn: async (email: string) =>
      await apiRequest("POST", "/api/auth/request-password-reset", { email }),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await resetFlight.run(() => resetMutation.mutateAsync(data.email));
    } catch {
      // Swallow errors — always show success to prevent enumeration
    } finally {
      setIsLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="flex flex-col items-center w-full max-w-md">
        <Card className="w-full">
          <CardHeader>
            <CardTitle style={{ fontFamily: FONT.serif }}>Reset your password</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  If an account exists for that email, we've sent a reset link. Check your inbox.
                </p>
                <p className="text-sm text-muted-foreground">
                  Didn't receive it? Check your spam folder or{" "}
                  <button
                    className="text-primary hover:underline"
                    onClick={() => setSubmitted(false)}
                  >
                    try again
                  </button>
                  .
                </p>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="you@example.com"
                            disabled={isLoading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={isLoading || resetFlight.inFlight} data-testid="button-send-reset">
                    {isLoading ? "Sending..." : "Send reset link"}
                  </Button>
                </form>
              </Form>
            )}
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-muted-foreground">
              Remember your password?{" "}
              <Link href="/login">
                <span className="text-primary hover:underline cursor-pointer">Sign in</span>
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
